import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Watch, CheckCircle2, Trophy, TrendingUp, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Wearables = () => {
  const [wearables, setWearables] = useState([]);
  const [verifiedWorkouts, setVerifiedWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchWearableData();
  }, []);

  const fetchWearableData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [wearablesRes, workoutsRes] = await Promise.all([
        axios.get(`${API}/wearables`, { headers }),
        axios.get(`${API}/workouts/verified`, { headers })
      ]);

      setWearables(wearablesRes.data);
      setVerifiedWorkouts(workoutsRes.data);
    } catch (error) {
      console.error('Error fetching wearable data:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectWearable = async (type) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.post(`${API}/wearables/connect`, { wearable_type: type }, { headers });
      
      toast.success(`${type} connected! Your workouts will now be verified.`);
      fetchWearableData();
      
      // Update user in localStorage
      const userData = JSON.parse(localStorage.getItem('user'));
      userData.wearable_connected = true;
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      toast.error('Failed to connect wearable');
    }
  };

  const disconnectWearable = async (wearableId) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(`${API}/wearables/${wearableId}`, { headers });
      toast.success('Wearable disconnected');
      fetchWearableData();
    } catch (error) {
      toast.error('Failed to disconnect wearable');
    }
  };

  const wearableTypes = [
    { id: 'apple_health', name: 'Apple Health', icon: '🍎', description: 'Apple Watch & iPhone Health' },
    { id: 'whoop', name: 'Whoop', icon: '💪', description: 'Recovery & Strain Tracking' },
    { id: 'fitbit', name: 'Fitbit', icon: '⌚', description: 'Activity & Heart Rate' },
    { id: 'garmin', name: 'Garmin', icon: '🏃', description: 'GPS & Performance' },
    { id: 'manual', name: 'Manual Entry', icon: '✍️', description: 'Log workouts manually' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-primary text-xl font-heading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6 md:p-8 lg:p-12">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-white tracking-tighter-xl">
            WEARABLE INTEGRATION
          </h1>
          <p className="text-muted-foreground mt-2">Track verified workouts and earn transformation badges</p>
        </div>

        {/* Verified Transformation Badge */}
        {verifiedWorkouts.length >= 10 && (
          <Card className="bg-gradient-to-r from-accent/20 to-primary/20 border-accent" data-testid="verified-badge-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <Trophy className="w-12 h-12 text-accent" />
                <div className="flex-1">
                  <h3 className="text-2xl font-heading font-black text-white">VERIFIED TRANSFORMATION</h3>
                  <p className="text-gray-300">
                    {verifiedWorkouts.length} verified workouts tracked • Share your proof on social media
                  </p>
                </div>
                <Button className="bg-accent hover:bg-accent/90 text-black font-bold uppercase">
                  Share Badge
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Connected Wearables */}
        <Card className="bg-card border-border" data-testid="connected-wearables-card">
          <CardHeader>
            <CardTitle className="text-xl font-heading font-bold text-white flex items-center gap-2">
              <Watch className="w-5 h-5 text-primary" />
              Connected Devices
            </CardTitle>
            <CardDescription>
              Automatically verify workouts with connected wearables
            </CardDescription>
          </CardHeader>
          <CardContent>
            {wearables.length > 0 ? (
              <div className="space-y-3">
                {wearables.map((wearable) => {
                  const typeInfo = wearableTypes.find(t => t.id === wearable.wearable_type);
                  return (
                    <div key={wearable.id} className="flex items-center justify-between p-4 bg-background rounded-sm border border-primary/30">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{typeInfo?.icon}</span>
                        <div>
                          <p className="font-medium text-white">{typeInfo?.name}</p>
                          <p className="text-xs text-muted-foreground">
                            Connected {new Date(wearable.connected_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className="bg-green-500/10 text-green-500 border-green-500/30">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Active
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => disconnectWearable(wearable.id)}
                          className="text-muted-foreground hover:text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Watch className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No wearables connected yet</p>
                <p className="text-sm mt-1">Connect a device to start tracking verified workouts</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Available Wearables */}
        <div className="space-y-4">
          <h2 className="text-2xl font-heading font-black text-white">Available Integrations</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {wearableTypes.map((type) => {
              const isConnected = wearables.some(w => w.wearable_type === type.id);
              return (
                <Card
                  key={type.id}
                  className={`bg-card border-border hover:border-primary/50 transition-colors ${isConnected ? 'opacity-50' : ''}`}
                  data-testid={`wearable-${type.id}`}
                >
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-4xl">{type.icon}</span>
                      {isConnected && (
                        <Badge className="bg-green-500/10 text-green-500 border-green-500/30">
                          Connected
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-lg font-heading font-bold">{type.name}</CardTitle>
                    <CardDescription>{type.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button
                      data-testid={`connect-${type.id}-button`}
                      onClick={() => connectWearable(type.id)}
                      disabled={isConnected}
                      className="w-full bg-primary hover:bg-primary/90 text-white font-bold uppercase"
                    >
                      {isConnected ? 'Connected' : 'Connect'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Verified Workouts History */}
        <Card className="bg-card border-border" data-testid="verified-workouts-card">
          <CardHeader>
            <CardTitle className="text-xl font-heading font-bold text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Verified Workout History
            </CardTitle>
            <CardDescription>
              Workouts automatically tracked from your connected devices
            </CardDescription>
          </CardHeader>
          <CardContent>
            {verifiedWorkouts.length > 0 ? (
              <div className="space-y-2">
                {verifiedWorkouts.map((workout) => (
                  <div key={workout.id} className="flex justify-between items-center p-3 bg-background rounded-sm border border-border">
                    <div>
                      <p className="text-sm font-medium text-white capitalize">{workout.workout_type}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(workout.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-mono font-bold text-primary">{workout.duration_minutes}min</p>
                        {workout.heart_rate_avg && (
                          <p className="text-xs text-muted-foreground">{workout.heart_rate_avg} bpm avg</p>
                        )}
                      </div>
                      {workout.verified && (
                        <CheckCircle2 className="w-5 h-5 text-green-500" data-testid="verified-icon" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <p className="text-sm">No verified workouts yet</p>
                <p className="text-xs mt-2">Connect a wearable and complete workouts to start tracking</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Benefits */}
        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/30">
          <CardContent className="p-6">
            <h3 className="text-xl font-heading font-bold text-white mb-4">Why Connect a Wearable?</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <CheckCircle2 className="w-6 h-6 text-primary mb-2" />
                <p className="text-sm font-medium text-white">Auto-Verify Workouts</p>
                <p className="text-xs text-muted-foreground">Proof of completion with heart rate data</p>
              </div>
              <div>
                <Trophy className="w-6 h-6 text-accent mb-2" />
                <p className="text-sm font-medium text-white">Earn Badges</p>
                <p className="text-xs text-muted-foreground">Share verified transformation on social</p>
              </div>
              <div>
                <TrendingUp className="w-6 h-6 text-secondary mb-2" />
                <p className="text-sm font-medium text-white">Track Progress</p>
                <p className="text-xs text-muted-foreground">See real-time performance metrics</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Wearables;
